import React, { createContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { ApiConfig } from '../../api/apiConfig/apiConfig';
import { DISABLE_ALL_NETWORK } from '../../networkConfig';
import { alertMessage } from '../CustomAlertMessage';

const SPOT_TRADE_QUOTE_SUFFIXES = ['USDT', 'USDC', 'BTC', 'ETH', 'BNB', 'USD'];

function splitSpotTradePair(pair) {
  const p = String(pair || '').trim().toUpperCase();
  for (const quote of SPOT_TRADE_QUOTE_SUFFIXES) {
    if (p.endsWith(quote) && p.length > quote.length) {
      return { base: p.slice(0, -quote.length), quote };
    }
  }
  return { base: p, quote: '' };
}

function getStoredUserId() {
  try {
    return localStorage.getItem('userId') || sessionStorage.getItem('userId') || null;
  } catch {
    return null;
  }
}

/** JWT from localStorage or sessionStorage (remember-me vs session login). */
function getSocketAuthFromStorage() {
  try {
    const rawToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    const accessToken = rawToken ? String(rawToken).replace(/^Bearer\s+/i, '') : null;
    if (!accessToken) return null;
    return { token: `Bearer ${accessToken}`, accessToken };
  } catch {
    return null;
  }
}

function applySocketAuth(sock) {
  if (!sock) return;
  const auth = getSocketAuthFromStorage();
  sock.auth = auth || {};
}

function formatSpotTradeExecutedMessage(trade, userId) {
  const qty = String(trade?.quantity ?? '').trim();
  const price = String(trade?.price ?? '').trim();
  const { base, quote } = splitSpotTradePair(trade?.pair);
  const quoteSuffix = quote ? ` ${quote}` : '';
  const isBuyer = userId && trade?.buyer_user_id && String(trade.buyer_user_id) === String(userId);
  const side = isBuyer ? 'Buy' : 'Sell';
  if (qty && price && base) {
    return `${side} order filled :: ${qty} ${base} @ ${price}${quoteSuffix}`;
  }
  if (base) {
    return `${side} order filled :: ${base}${quoteSuffix}`;
  }
  return `${side} order filled ::`;
}

export const SocketContext = createContext();

// =====================================================================
// 🔧 SOCKET CONFIGURATION
// =====================================================================
const SOCKET_CONFIG = {
  transports: ['websocket'],
  upgrade: false,
  rejectUnauthorized: false,
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  timeout: 20000,
};

const SocketContextProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [marketData, setMarketData] = useState({ pairs: [], futures_pairs: [], hot_pairs_chart: {} });
  const [convertRates, setConvertRates] = useState(null);
  const [exchangeData, setExchangeData] = useState(null);
  const [futuresData, setFuturesData] = useState(null);
  const [futuresPrice, setFuturesPrice] = useState(null); // sub-second price ticks
  /** User portfolio — pushed on connect and via futures:update (no REST poll for history tabs). */
  const [futuresPortfolioOrders, setFuturesPortfolioOrders] = useState([]);
  const [futuresPortfolioPositions, setFuturesPortfolioPositions] = useState([]);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const socketRef = useRef(null);
  const seenSpotTradeIds = useRef(new Set());

  // Track pending subscriptions to apply when socket connects
  const pendingSubscriptions = useRef({
    market: false,
    convert: false,
    exchange: null,
    futures: null
  });

  const reemitPendingSubscriptions = useCallback((sock) => {
    if (!sock?.connected) return;
    const pending = pendingSubscriptions.current;
    if (pending.market) sock.emit('market:subscribe');
    if (pending.convert) sock.emit('convert:subscribe');
    if (pending.exchange) sock.emit('exchange:subscribe', pending.exchange);
    if (pending.futures) sock.emit('futures:subscribe', pending.futures);
  }, []);

  // =====================================================================
  // 🔗 SOCKET CONNECTION
  // =====================================================================
  const connectSocket = useCallback(() => {
    if (DISABLE_ALL_NETWORK) {
      return null;
    }
    // Don't create multiple connections - check if socket exists (connected or connecting)
    if (socketRef.current) {
      // Always refresh handshake auth from storage before any reconnect.
      applySocketAuth(socketRef.current);
      // If already connected, just return
      if (socketRef.current.connected) {
        return socketRef.current;
      }
      // If socket exists but disconnected, try to reconnect
      if (!socketRef.current.connected && !socketRef.current.connecting) {
        socketRef.current.connect();
      }
      return socketRef.current;
    }

    const auth = getSocketAuthFromStorage();

    const newSocket = io(`${ApiConfig?.webSocketUrl}`, {
      ...SOCKET_CONFIG,
      auth: auth || undefined,
    });

    socketRef.current = newSocket;

    // =====================================================================
    // 📡 CONNECTION EVENTS
    // =====================================================================
    newSocket.on('connect', () => {
      setIsConnected(true);
      reconnectAttempts.current = 0;
      reemitPendingSubscriptions(newSocket);
    });

    // Auto-reconnect must pick up login / refreshed JWT (handshake auth only).
    newSocket.io.on('reconnect_attempt', () => {
      applySocketAuth(newSocket);
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
      setFuturesPortfolioOrders([]);
      setFuturesPortfolioPositions([]);

      // Auto reconnect for certain disconnect reasons
      if (reason === 'io server disconnect') {
        applySocketAuth(newSocket);
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (error) => {
      setIsConnected(false);
      attemptReconnect();
    });

    newSocket.on('error', (error) => {
    });

    // =====================================================================
    // 📊 MARKET DATA EVENTS (Server-push)
    // =====================================================================
    newSocket.on('market:update', (data) => {
      // Server may send a full snapshot or a partial (e.g. hot_pairs_chart only). Do not
      // clear pairs / futures when those keys are omitted.
      if (!data || typeof data !== 'object') return;
      setMarketData((prev) => ({
        pairs: data.pairs === undefined ? prev.pairs : (data.pairs || []),
        futures_pairs: data.futures_pairs === undefined ? prev.futures_pairs : (data.futures_pairs || []),
        hot_pairs_chart:
          data.hot_pairs_chart === undefined
            ? prev.hot_pairs_chart
            : { ...prev.hot_pairs_chart, ...data.hot_pairs_chart }
      }));
    });

    newSocket.on('convert:rates', (data) => {
      if (!data || typeof data !== 'object') return;
      setConvertRates(data);
    });

    newSocket.on('kyc:status', () => {
      try {
        window.dispatchEvent(new CustomEvent('agce-kyc-status'));
      } catch {
        /* ignore */
      }
    });

    // =====================================================================
    // 💱 EXCHANGE DATA EVENTS (Server-push)
    // =====================================================================
    newSocket.on('exchange:update', (data) => {
      if (data) {
        setExchangeData(data);
      }
    });

    // =====================================================================
    // 📈 FUTURES DATA EVENTS (Server-push)
    // =====================================================================
    // Full payload (orderbook, trades, orders, positions, balance)
    newSocket.on('futures:update', (data) => {
      if (!data) return;
      setFuturesData(data);
      if (data.open_orders !== undefined) {
        setFuturesPortfolioOrders(Array.isArray(data.open_orders) ? data.open_orders : []);
      }
      if (data.open_positions !== undefined) {
        setFuturesPortfolioPositions(Array.isArray(data.open_positions) ? data.open_positions : []);
      }
    });

    // Portfolio snapshot — all open orders (on connect + after order events)
    newSocket.on('futures:open_orders', (payload) => {
      const orders = payload?.orders ?? payload;
      if (Array.isArray(orders)) setFuturesPortfolioOrders(orders);
    });

    // Portfolio snapshot — all open positions (on connect + after position events)
    newSocket.on('futures:open_positions', (payload) => {
      const positions = payload?.positions ?? payload;
      if (Array.isArray(positions)) setFuturesPortfolioPositions(positions);
    });

    // Sub-second price tick (mark_price, last_price, index_price only)
    newSocket.on('futures:price', (data) => {
      if (data) setFuturesPrice(data);
    });

    // User-specific spot trade fills — event name: spot:trade:<PAIR>
    newSocket.onAny((eventName, ...args) => {
      if (!eventName || !String(eventName).startsWith('spot:trade:')) return;

      const trade = args[0];
      if (!trade || typeof trade !== 'object') return;

      const userId = getStoredUserId();
      if (!userId) return;

      const isBuyer = trade.buyer_user_id && String(trade.buyer_user_id) === String(userId);
      const isSeller = trade.seller_user_id && String(trade.seller_user_id) === String(userId);
      if (!isBuyer && !isSeller) return;

      const tradeId = trade.trade_id || trade.execution_id;
      if (tradeId) {
        const key = String(tradeId);
        if (seenSpotTradeIds.current.has(key)) return;
        seenSpotTradeIds.current.add(key);
        if (seenSpotTradeIds.current.size > 200) {
          const oldest = seenSpotTradeIds.current.values().next().value;
          seenSpotTradeIds.current.delete(oldest);
        }
      }

      alertMessage(formatSpotTradeExecutedMessage(trade, userId));
    });

    setSocket(newSocket);
    return newSocket;
  }, [reemitPendingSubscriptions]);

  // =====================================================================
  // 🔄 RECONNECTION LOGIC
  // =====================================================================
  const attemptReconnect = useCallback(() => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      return;
    }

    const delay = Math.min(2000 * Math.pow(2, reconnectAttempts.current), 10000);
    reconnectAttempts.current += 1;


    setTimeout(() => {
      if (!socketRef.current?.connected) {
        applySocketAuth(socketRef.current);
        connectSocket();
      }
    }, delay);
  }, [connectSocket]);

  // =====================================================================
  // 📊 SUBSCRIPTION HELPERS
  // =====================================================================
  const subscribeToMarket = useCallback(() => {
    pendingSubscriptions.current.market = true;
    if (socketRef.current?.connected) {
      socketRef.current.emit('market:subscribe');
    }
  }, []);

  const unsubscribeFromMarket = useCallback(() => {
    pendingSubscriptions.current.market = false;
    if (socketRef.current?.connected) {
      socketRef.current.emit('market:unsubscribe');
    }
  }, []);

  const subscribeToConvertRates = useCallback(() => {
    pendingSubscriptions.current.convert = true;
    if (socketRef.current?.connected) {
      socketRef.current.emit('convert:subscribe');
    }
  }, []);

  const unsubscribeFromConvertRates = useCallback(() => {
    pendingSubscriptions.current.convert = false;
    if (socketRef.current?.connected) {
      socketRef.current.emit('convert:unsubscribe');
    }
  }, []);

  // Track current exchange subscription to avoid duplicates
  const currentExchangeSubscription = useRef(null);

  const subscribeToExchange = useCallback((baseCurrencyId, quoteCurrencyId, extraParams = {}) => {
    // If no pair specified, request pairs list only (for initial page load)
    if (!baseCurrencyId || !quoteCurrencyId) {
      if (socketRef.current?.connected) {
        socketRef.current.emit('exchange:subscribe', {});
      }
      return;
    }

    const tradeType = extraParams.tradeType || "spot";
    const subKey = `${baseCurrencyId}-${quoteCurrencyId}-${tradeType}`;

    // Avoid duplicate subscription to the same pair + mode
    if (currentExchangeSubscription.current === subKey) {
      return;
    }

    // Pair changed: drop the previous pair's snapshot so consumers (order book, etc.)
    // can't see stale buy_order/sell_order between pair switch and the new server snapshot.
    setExchangeData(null);

    currentExchangeSubscription.current = subKey;
    const payload = { base_currency_id: baseCurrencyId, quote_currency_id: quoteCurrencyId, ...extraParams };
    pendingSubscriptions.current.exchange = payload;

    if (socketRef.current?.connected) {
      socketRef.current.emit('exchange:subscribe', payload);
    }
  }, []);

  const unsubscribeFromExchange = useCallback((baseCurrencyId, quoteCurrencyId) => {
    currentExchangeSubscription.current = null;
    pendingSubscriptions.current.exchange = null;
    setExchangeData(null);
    if (socketRef.current?.connected) {
      socketRef.current.emit('exchange:unsubscribe', {
        base_currency_id: baseCurrencyId,
        quote_currency_id: quoteCurrencyId
      });
    }
  }, []);

  // subscribeToFutures(payload)
  //   payload = {}                          → contracts list only (type: "contracts_only")
  //   payload = { symbol: "BTCUSDT-PERP" } → continuous updates for that symbol
  const subscribeToFutures = useCallback((payload = {}) => {
    pendingSubscriptions.current.futures = payload;
    if (socketRef.current?.connected) {
      socketRef.current.emit('futures:subscribe', payload);
    }
  }, []);

  const unsubscribeFromFutures = useCallback(() => {
    pendingSubscriptions.current.futures = null;
    if (socketRef.current?.connected) {
      socketRef.current.emit('futures:unsubscribe');
    }
  }, []);

  // futures:set_history_tab – atomic history tab switch (orders | trades | positions | null)
  const setFuturesHistoryTab = useCallback((tab, skip = 0, limit = 50) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('futures:set_history_tab', {
        tab,
        skip: Math.max(0, skip),
        limit: Math.min(100, Math.max(1, limit))
      });
    }
  }, []);

  // =====================================================================
  // 🔐 AUTH — re-authenticate socket after login / token change
  // =====================================================================
  const authenticateSocket = useCallback(() => {
    if (DISABLE_ALL_NETWORK) return;

    const newAuth = getSocketAuthFromStorage() || {};

    // Allow spot to re-subscribe after auth (duplicate-sub guard would skip otherwise).
    currentExchangeSubscription.current = null;

    if (socketRef.current) {
      socketRef.current.auth = newAuth;

      if (socketRef.current.connected) {
        socketRef.current.disconnect().connect();
      } else {
        socketRef.current.connect();
      }
    } else {
      connectSocket();
    }
  }, [connectSocket]);

  // =====================================================================
  // 🚀 INITIALIZATION
  // =====================================================================
  useEffect(() => {
    const activeSocket = connectSocket();

    const handleVisibilityChange = () => {
      if (DISABLE_ALL_NETWORK) return;
      if (document.visibilityState !== 'visible') return;

      reconnectAttempts.current = 0;
      const auth = getSocketAuthFromStorage();
      applySocketAuth(socketRef.current);

      if (!socketRef.current?.connected) {
        if (auth) authenticateSocket();
        else connectSocket();
        return;
      }

      // Connected as guest while a JWT exists (stale handshake after idle reconnect).
      if (auth?.accessToken && !socketRef.current.auth?.accessToken) {
        authenticateSocket();
        return;
      }

      // Still connected with auth: refresh private channel snapshots.
      reemitPendingSubscriptions(socketRef.current);
    };

    const onAuthChanged = () => authenticateSocket();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('agce-auth-changed', onAuthChanged);
    window.addEventListener('agce-auth-token-changed', onAuthChanged);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('agce-auth-changed', onAuthChanged);
      window.removeEventListener('agce-auth-token-changed', onAuthChanged);
      if (activeSocket) {
        activeSocket.disconnect();
      }
    };
  }, [connectSocket, authenticateSocket, reemitPendingSubscriptions]);
  // =====================================================================
  // 🎨 SLIDER SETTINGS (unchanged)
  // =====================================================================
  const settings = {
    centerMode: true,
    centerPadding: "30px",
    dots: false,
    infinite: true,
    autoplay: true,
    speed: 4000,
    autoplaySpeed: 0,
    arrows: false,
    cssEase: "linear",
    slidesToShow: 6,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 768, settings: { centerPadding: '2px', slidesToShow: 3, slidesToScroll: 'auto' } },
      { breakpoint: 1199, settings: { slidesToShow: 4, slidesToScroll: 'auto' } },
    ],
  };

  const settingstwo = {
    dots: true,
    infinite: true,
    autoplay: true,
    speed: 600,
    autoplaySpeed: 2000,
    arrows: false,
    slidesToShow: 3,
    slidesToScroll: 3,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 3, dots: true } },
      { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 2, initialSlide: 2 } },
      { breakpoint: 480, settings: { slidesToShow: 2, slidesToScroll: 2 } },
    ]
  };

  const Sliderpartners = {
    dots: false,
    infinite: true,
    slidesToShow: 6,
    slidesToScroll: 1,
    autoplay: true,
    speed: 600,
    autoplaySpeed: 2000,
    arrows: false,
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: 4, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 2, slidesToScroll: 1 } },
    ],
  };

  const Sliderpartners2 = {
    dots: false,
    infinite: true,
    slidesToShow: 6,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 0,
    arrows: false,
    speed: 8000,
    pauseOnHover: false,
    cssEase: "linear",
    rtl: true,
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: 4, slidesToScroll: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 2, slidesToScroll: 1 } },
    ],
  };

  // =====================================================================
  // 📤 CONTEXT VALUE
  // =====================================================================

  // Expose getSocket function for external components (like chart) to use the same socket
  const getSocket = useCallback(() => {
    return socketRef.current;
  }, []);

  const contextValue = {
    socket,
    isConnected,
    marketData,
    convertRates,
    exchangeData,
    futuresData,
    futuresPrice,
    futuresPortfolioOrders,
    futuresPortfolioPositions,
    subscribeToMarket,
    unsubscribeFromMarket,
    subscribeToConvertRates,
    unsubscribeFromConvertRates,
    subscribeToExchange,
    unsubscribeFromExchange,
    subscribeToFutures,
    unsubscribeFromFutures,
    setFuturesHistoryTab,
    getSocket,
    connectSocket,
    authenticateSocket,
    settings,
    settingstwo,
    Sliderpartners,
    Sliderpartners2
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContextProvider;
